import React, { useMemo, useState } from "react";
import { createEmptySection } from "./formBuilderFieldFactory";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AiOutlinePlus } from "react-icons/ai";
import { FiLayers, FiMenu, FiSearch } from "react-icons/fi";
import { parseBooleanVal } from "Utils/utils";
import { DropDown } from "components/common/DropDown/DropDown";
import FormBuilderFieldCanvas from "./FormBuilderFieldCanvas";
import {
  FIELD_TYPE_LABELS,
  PALETTE_FIELD_GROUPS,
  PALETTE_GROUP_THEMES,
  PALETTE_STRUCTURE_ITEMS,
  fieldTypeLabel,
} from "./formBuilderConstants";
import { PaletteFieldMock } from "./formBuilderPaletteMock";
import {
  addPaletteField,
  filterCollisionsForActive,
  getActiveDragKind,
  moveExistingField,
  parseDragId,
  reorderSections,
  resolveDropTarget,
} from "./formBuilderMutations";

const PaletteItem = ({
  item,
  groupId,
  disabled = false,
  onQuickAdd,
  variant = "field",
}) => {
  const Icon = item.icon;
  const themeClass = PALETTE_GROUP_THEMES[groupId] ?? "theme-blue";
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${item.type}`,
    disabled,
    data: { type: "palette", fieldType: item.type },
  });

  return (
    <div
      ref={setNodeRef}
      className={`form-builder-palette-item-wrap ${themeClass} ${
        variant === "structure" ? "is-structure" : ""
      } ${isDragging ? "is-dragging" : ""}`}
    >
      <button
        type="button"
        className={`form-builder-palette-chip ${
          variant === "structure" ? "is-structure" : ""
        }`}
        {...attributes}
        {...listeners}
        disabled={disabled}
        onDoubleClick={() => !disabled && onQuickAdd?.(item.type)}
      >
        <span className="form-builder-palette-chip-icon">
          <Icon aria-hidden="true" />
        </span>
        <span className="form-builder-palette-chip-copy">
          <span className="form-builder-palette-chip-label">{item.label}</span>
          <span className="form-builder-palette-chip-desc">{item.description}</span>
        </span>
        {variant === "field" && <PaletteFieldMock type={item.type} />}
      </button>
      {!disabled && (
        <button
          type="button"
          className="form-builder-palette-quick-add"
          aria-label={`Add ${item.label} with field form`}
          title="Open field form"
          onClick={(event) => {
            event.stopPropagation();
            onQuickAdd?.(item.type);
          }}
        >
          <AiOutlinePlus />
        </button>
      )}
    </div>
  );
};

const FormBuilderPalette = ({
  disabled = false,
  searchTerm,
  onSearchChange,
  onQuickAdd,
}) => {
  const filterItems = (items, query) => {
    if (!query) return items;
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
    );
  };

  const filteredStructure = useMemo(
    () => filterItems(PALETTE_STRUCTURE_ITEMS, searchTerm.trim().toLowerCase()),
    [searchTerm]
  );

  const filteredGroups = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return PALETTE_FIELD_GROUPS;

    return PALETTE_FIELD_GROUPS.map((group) => ({
      ...group,
      items: filterItems(group.items, query),
    })).filter((group) => group.items.length > 0);
  }, [searchTerm]);

  return (
    <aside className="form-builder-palette">
      <div className="form-builder-palette-header">
        <div className="form-builder-palette-header-top">
          <span className="form-builder-palette-badge">Palette</span>
          <h3 className="form-builder-palette-title">Components</h3>
          <p className="form-builder-palette-subtitle">
            Drag onto the canvas. Use{" "}
            <span className="form-builder-palette-hint-plus">+</span> to open the detailed field
            form.
          </p>
        </div>
        <div className="form-builder-palette-search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search components"
          />
        </div>
      </div>

      <div className="form-builder-palette-groups">
        {filteredStructure.length === 0 && filteredGroups.length === 0 ? (
          <p className="form-builder-palette-no-results">No components match your search.</p>
        ) : (
          <>
            {filteredStructure.length > 0 && (
              <section className="form-builder-palette-structure theme-teal">
                <h4 className="form-builder-palette-structure-label">Structure</h4>
                <p className="form-builder-palette-structure-hint">
                  Build the form skeleton first
                </p>
                <div className="form-builder-palette-structure-items">
                  {filteredStructure.map((item) => (
                    <PaletteItem
                      key={item.type}
                      item={item}
                      groupId="structure"
                      variant="structure"
                      disabled={disabled}
                      onQuickAdd={onQuickAdd}
                    />
                  ))}
                </div>
              </section>
            )}

            {filteredGroups.map((group) => (
              <section
                key={group.id}
                className={`form-builder-palette-group ${
                  PALETTE_GROUP_THEMES[group.id] ?? "theme-blue"
                }`}
              >
                <h4 className="form-builder-palette-group-label">
                  <span className="form-builder-palette-group-dot" aria-hidden="true" />
                  {group.label}
                </h4>
                <div className="form-builder-palette-items">
                  {group.items.map((item) => (
                    <PaletteItem
                      key={item.type}
                      item={item}
                      groupId={group.id}
                      disabled={disabled}
                      onQuickAdd={onQuickAdd}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </aside>
  );
};

const stopDragPointerDown = (event) => {
  event.stopPropagation();
};

const DropZone = ({ id, children, className = "", isEmpty = false, isDropTarget = false }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`form-builder-drop-zone ${className} ${
        isOver || isDropTarget ? "is-over" : ""
      } ${isEmpty ? "is-empty" : ""}`}
    >
      {children}
    </div>
  );
};

const SortableFieldRow = ({
  id,
  field,
  sectionIndex,
  fieldIndex,
  subFieldIndex,
  onEdit,
  onDelete,
  isDropTarget = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({
      id,
      data: {
        type: subFieldIndex != null ? "subfield" : "field",
        sectionIndex,
        fieldIndex,
        subFieldIndex,
      },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const widthClass =
    field.type === "content" || field.type === "table"
      ? "col-span-12"
      : parseBooleanVal(field.isFullWidth)
      ? "col-span-12"
      : "col-span-12 md:col-span-6";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`form-builder-field-row ${widthClass} type-${field.type} ${
        isDragging ? "is-dragging" : ""
      } ${isOver || isDropTarget ? "is-drop-target" : ""}`}
      data-field-type={field.type}
      {...attributes}
      {...listeners}
    >
      <div className="form-builder-field-toolbar">
        <span className="form-builder-drag-handle" aria-hidden="true">
          <FiMenu />
        </span>
        <span className="form-builder-field-type-badge">
          {fieldTypeLabel(field.type)}
          {field.required && " · Required"}
        </span>
        {field.needs_review && (
          <span className="form-builder-review-badge">Needs review</span>
        )}
        <div className="form-builder-field-toolbar-spacer" />
        <div
          className="form-builder-field-menu"
          onPointerDown={stopDragPointerDown}
        >
          <DropDown
            type={field.type === "sub_section" ? "sub_section" : undefined}
            openConfirmationModalHandler={onDelete}
            openEditSectionModalHandler={onEdit}
            item={field}
            sectionIndex={sectionIndex}
            fieldIndex={fieldIndex}
            subFieldIndex={subFieldIndex ?? null}
          />
        </div>
      </div>

      <button
        type="button"
        className="form-builder-field-canvas"
        onClick={() => onEdit(sectionIndex, fieldIndex, field, subFieldIndex ?? null)}
      >
        <FormBuilderFieldCanvas field={field} builderMode />
      </button>
    </div>
  );
};

const SortableSubSection = ({
  id,
  field,
  sectionIndex,
  fieldIndex,
  children,
  onEdit,
  onDelete,
  isDropTarget = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({
      id,
      data: {
        type: "field",
        sectionIndex,
        fieldIndex,
      },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`form-subsection form-builder-subsection-wrap col-span-12 ${
        isDragging ? "is-dragging" : ""
      } ${isOver || isDropTarget ? "is-drop-target" : ""}`}
    >
      <div
        className="form-builder-subsection-header form-subsection-header form-builder-draggable-header"
        {...attributes}
        {...listeners}
      >
        <span className="form-builder-drag-handle" aria-hidden="true">
          <FiMenu />
        </span>
        <div className="form-builder-subsection-heading">
          <h4 className="form-subsection-title">{field.section_title || "Sub-section"}</h4>
          {parseBooleanVal(field.isAddOther) && (
            <span className="form-builder-duplicate-badge">Duplicates allowed</span>
          )}
        </div>
        <div
          className="form-builder-field-menu"
          onPointerDown={stopDragPointerDown}
        >
          <DropDown
            type="sub_section"
            openConfirmationModalHandler={onDelete}
            openEditSectionModalHandler={onEdit}
            item={field}
            sectionIndex={sectionIndex}
            fieldIndex={fieldIndex}
            subFieldIndex={null}
          />
        </div>
      </div>
      {children}
    </div>
  );
};

const SortableSection = ({
  id,
  section,
  sectionIndex,
  children,
  onDeleteSection,
  openEditSectionTitleModal,
  isDropTarget = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
      data: { type: "section", sectionIndex },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`form-builder-section ${isDragging ? "is-dragging" : ""} ${
        isDropTarget ? "is-drop-target" : ""
      }`}
    >
      <header
        className="form-builder-section-header form-builder-draggable-header"
        {...attributes}
        {...listeners}
      >
        <span className="form-builder-drag-handle" aria-hidden="true">
          <FiMenu />
        </span>
        <span className="form-builder-section-index" aria-hidden="true">
          {sectionIndex + 1}
        </span>
        <div className="form-builder-section-heading flex-1 min-w-0">
          <h3 className="form-builder-section-title">{section.section_title}</h3>
          <p className="form-builder-section-count">
            {section.input_fields?.length ?? 0} field
            {(section.input_fields?.length ?? 0) !== 1 ? "s" : ""}
          </p>
        </div>
        <div
          className="form-builder-field-menu"
          onPointerDown={stopDragPointerDown}
        >
          <DropDown
            type="section"
            openConfirmationModalHandler={onDeleteSection}
            openEditSectionModalHandler={openEditSectionTitleModal}
            item={section}
            sectionIndex={sectionIndex}
            fieldIndex="null"
            subFieldIndex="null"
          />
        </div>
      </header>

      <DropZone
        id={`zone:section:${sectionIndex}`}
        className="form-builder-section-body form-field-grid form-builder-canvas-grid"
        isEmpty={!(section.input_fields ?? []).length}
        isDropTarget={isDropTarget}
      >
        {!(section.input_fields ?? []).length && (
          <div className="form-builder-section-empty">
            Drag components from the palette into this section
          </div>
        )}
        {children}
      </DropZone>
    </article>
  );
};

const DragPreview = ({ activeId, template }) => {
  const parsed = parseDragId(String(activeId));
  if (!parsed) return null;

  if (parsed.kind === "palette") {
    const label = FIELD_TYPE_LABELS[parsed.fieldType] ?? parsed.fieldType;
    return (
      <div className="form-builder-drag-overlay">
        <span className="form-builder-drag-overlay-badge">Add</span>
        {label}
      </div>
    );
  }

  if (parsed.kind === "section") {
    const section = template[parsed.sectionIndex];
    return (
      <div className="form-builder-drag-overlay">
        <span className="form-builder-drag-overlay-badge">Section</span>
        {section?.section_title ?? "Section"}
      </div>
    );
  }

  if (parsed.kind === "field") {
    const field = template[parsed.sectionIndex]?.input_fields?.[parsed.fieldIndex];
    return (
      <div className="form-builder-drag-overlay">
        <span className="form-builder-drag-overlay-badge">Field</span>
        {field?.lable ?? field?.section_title ?? "Field"}
      </div>
    );
  }

  if (parsed.kind === "subfield") {
    const field =
      template[parsed.sectionIndex]?.input_fields?.[parsed.fieldIndex]?.sub_fields?.[
        parsed.subFieldIndex
      ];
    return (
      <div className="form-builder-drag-overlay">
        <span className="form-builder-drag-overlay-badge">Field</span>
        {field?.lable ?? "Field"}
      </div>
    );
  }

  return <div className="form-builder-drag-overlay">Move item</div>;
};

const formBuilderCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  const filteredPointer = filterCollisionsForActive(args.active?.id, pointerCollisions);
  if (filteredPointer.length) return filteredPointer;

  const centerCollisions = closestCenter(args);
  return filterCollisionsForActive(args.active?.id, centerCollisions);
};

export const FormBuilderSectionList = ({
  template,
  setTemplate,
  openInputModalHandler,
  openSubSectionModalHandler,
  openSubSectionInputModalHandler,
  openEditSectionTitleModal,
  openEditSectionModalHandler,
  openEditSubSectionModalHandler,
  openSectionTitleConfirmationModalHandler,
  openSubSectionConfirmationModalHandler,
  openConfirmationModalHandler,
  onLegacyAddFieldRequest,
  openSectionModalHandler,
  importReview = null,
  onDismissImportReview,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [paletteSearch, setPaletteSearch] = useState("");
  const [focusedSectionIndex, setFocusedSectionIndex] = useState(0);
  const [focusedSubSectionFieldIndex, setFocusedSubSectionFieldIndex] =
    useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sectionIds = template.map((_, index) => `section:${index}`);
  const hasSections = template.length > 0;
  const activeDragKind = activeId ? getActiveDragKind(activeId) : null;

  const resetDragState = () => {
    setActiveId(null);
    setOverId(null);
  };

  const ensureSectionIndex = (target) => {
    if (target?.sectionIndex != null) return target.sectionIndex;
    if (hasSections) return focusedSectionIndex ?? template.length - 1;
    return 0;
  };

  const applyPaletteField = (fieldType, target) => {
    const sectionIndex = ensureSectionIndex(target);

    if (fieldType === "section") {
      const nextSection = createEmptySection(`Section ${template.length + 1}`);
      setTemplate([...template, nextSection]);
      setFocusedSectionIndex(template.length);
      return;
    }

    if (fieldType === "sub_section") {
      if (!hasSections) {
        setTemplate([createEmptySection("Section 1")]);
        setFocusedSectionIndex(0);
      }
      openSubSectionModalHandler(sectionIndex);
      return;
    }

    const result = addPaletteField(template, fieldType, target);
    if (!result) return;

    setTemplate(result.template);
    setFocusedSectionIndex(result.sectionIndex ?? sectionIndex);
  };

  const handleQuickAdd = (fieldType) => {
    if (fieldType === "section") {
      openSectionModalHandler?.();
      return;
    }

    const sectionIndex = hasSections
      ? focusedSectionIndex ?? template.length - 1
      : 0;

    if (!hasSections) {
      setTemplate([createEmptySection("Section 1")]);
      setFocusedSectionIndex(0);
    }

    if (fieldType === "sub_section") {
      openSubSectionModalHandler(sectionIndex);
      return;
    }

    if (onLegacyAddFieldRequest) {
      onLegacyAddFieldRequest(
        sectionIndex,
        fieldType,
        focusedSubSectionFieldIndex
      );
      return;
    }

    openInputModalHandler(sectionIndex);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setOverId(null);

    const parsed = parseDragId(String(event.active.id));
    if (parsed?.kind === "section" || parsed?.kind === "zone-section") {
      setFocusedSectionIndex(parsed.sectionIndex);
    }
    if (parsed?.kind === "field" || parsed?.kind === "subfield") {
      setFocusedSectionIndex(parsed.sectionIndex);
    }
  };

  const handleDragOver = (event) => {
    setOverId(event.over?.id ?? null);

    const overTarget = resolveDropTarget(event.over);
    if (overTarget?.sectionIndex != null) {
      setFocusedSectionIndex(overTarget.sectionIndex);
    }
    if (
      overTarget?.kind === "zone-subsection" ||
      overTarget?.kind === "subfield"
    ) {
      setFocusedSubSectionFieldIndex(overTarget.fieldIndex);
    } else if (overTarget?.kind !== "subfield") {
      setFocusedSubSectionFieldIndex(null);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      resetDragState();
      return;
    }

    const activeMeta = parseDragId(String(active.id));

    if (activeMeta?.kind === "palette") {
      applyPaletteField(activeMeta.fieldType, resolveDropTarget(over));
      resetDragState();
      return;
    }

    if (activeMeta?.kind === "section") {
      const reordered = reorderSections(template, active, over);
      if (reordered) {
        setTemplate(reordered.template);
      }
      resetDragState();
      return;
    }

    const moved = moveExistingField(template, active, over);
    if (moved) {
      setTemplate(moved.template);
    }

    resetDragState();
  };

  const totalFields = useMemo(
    () =>
      template.reduce(
        (count, section) => count + (section.input_fields?.length ?? 0),
        0
      ),
    [template]
  );

  const canvas = !hasSections ? (
    <DropZone id="zone:canvas" className="form-builder-empty-canvas">
      <div className="form-builder-empty-copy">
        <div className="form-builder-empty-icon" aria-hidden="true">
          <FiLayers />
        </div>
        <h3>Your form canvas is ready</h3>
        <p>
          Drag <strong>Section</strong> from the palette to start, then add fields and
          sub-sections the same way.
        </p>
      </div>
    </DropZone>
  ) : (
    <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
      <div className="form-builder-section-list">
        {template.map((section, sectionIndex) => {
          const fieldIds = (section.input_fields ?? []).map(
            (_, fieldIndex) => `field:${sectionIndex}:${fieldIndex}`
          );
          const sectionDropTarget =
            overId === `section:${sectionIndex}` ||
            overId === `zone:section:${sectionIndex}`;

          return (
            <SortableSection
              key={`section-${sectionIndex}`}
              id={`section:${sectionIndex}`}
              section={section}
              sectionIndex={sectionIndex}
              isDropTarget={sectionDropTarget && activeDragKind === "section"}
              openEditSectionTitleModal={openEditSectionTitleModal}
              onDeleteSection={openSectionTitleConfirmationModalHandler}
            >
              <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
                {(section.input_fields ?? []).map((field, fieldIndex) => {
                  if (field.type === "sub_section") {
                    const subFieldIds = (field.sub_fields ?? []).map(
                      (_, subFieldIndex) =>
                        `subfield:${sectionIndex}:${fieldIndex}:${subFieldIndex}`
                    );
                    const subsectionZoneId = `zone:subsection:${sectionIndex}:${fieldIndex}`;
                    const isSubsectionDropTarget =
                      overId === subsectionZoneId ||
                      subFieldIds.includes(String(overId));

                    return (
                      <SortableSubSection
                        key={`field-${sectionIndex}-${fieldIndex}`}
                        id={`field:${sectionIndex}:${fieldIndex}`}
                        field={field}
                        sectionIndex={sectionIndex}
                        fieldIndex={fieldIndex}
                        isDropTarget={
                          overId === `field:${sectionIndex}:${fieldIndex}` &&
                          ["palette", "field", "subfield"].includes(activeDragKind)
                        }
                        onEdit={(si, fi, item) =>
                          openEditSectionTitleModal(si, fi, item, null)
                        }
                        onDelete={openSubSectionConfirmationModalHandler}
                      >
                        <DropZone
                          id={subsectionZoneId}
                          className="form-subsection-body form-field-grid form-builder-canvas-grid"
                          isEmpty={!(field.sub_fields ?? []).length}
                          isDropTarget={
                            isSubsectionDropTarget &&
                            ["palette", "field", "subfield"].includes(activeDragKind)
                          }
                        >
                          {!(field.sub_fields ?? []).length && (
                            <div className="form-builder-section-empty is-compact">
                              Drag fields from the palette into this sub-section
                            </div>
                          )}

                          <SortableContext
                            items={subFieldIds}
                            strategy={verticalListSortingStrategy}
                          >
                            {(field.sub_fields ?? []).map((item, subFieldIndex) => (
                              <SortableFieldRow
                                key={`subfield-${sectionIndex}-${fieldIndex}-${subFieldIndex}`}
                                id={`subfield:${sectionIndex}:${fieldIndex}:${subFieldIndex}`}
                                field={item}
                                sectionIndex={sectionIndex}
                                fieldIndex={fieldIndex}
                                subFieldIndex={subFieldIndex}
                                isDropTarget={
                                  overId ===
                                    `subfield:${sectionIndex}:${fieldIndex}:${subFieldIndex}` &&
                                  ["palette", "field", "subfield"].includes(activeDragKind)
                                }
                                onEdit={openEditSubSectionModalHandler}
                                onDelete={openSubSectionConfirmationModalHandler}
                              />
                            ))}
                          </SortableContext>
                        </DropZone>
                      </SortableSubSection>
                    );
                  }

                  return (
                    <SortableFieldRow
                      key={`field-${sectionIndex}-${fieldIndex}`}
                      id={`field:${sectionIndex}:${fieldIndex}`}
                      field={field}
                      sectionIndex={sectionIndex}
                      fieldIndex={fieldIndex}
                      isDropTarget={
                        overId === `field:${sectionIndex}:${fieldIndex}` &&
                        ["palette", "field", "subfield"].includes(activeDragKind)
                      }
                      onEdit={openEditSectionModalHandler}
                      onDelete={openConfirmationModalHandler}
                    />
                  );
                })}
              </SortableContext>
            </SortableSection>
          );
        })}
      </div>
    </SortableContext>
  );

  return (
    <div className="form-builder-studio">
      <DndContext
        sensors={sensors}
        collisionDetection={formBuilderCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={resetDragState}
      >
        <FormBuilderPalette
          searchTerm={paletteSearch}
          onSearchChange={setPaletteSearch}
          onQuickAdd={handleQuickAdd}
        />
        <div className="form-builder-canvas-wrap">
          {importReview && (
            <div className="pdf-import-review" role="status">
              <div>
                <p className="pdf-import-review-title">
                  {importReview.documentKind === "scanned"
                    ? "Scanned PDF imported"
                    : importReview.documentKind === "interactive"
                    ? "Interactive PDF imported"
                    : "Text PDF imported"}
                  {importReview.reviewRequired ? " · Review before publishing" : ""}
                </p>
                {(importReview.warnings || []).slice(0, 2).map((warning) => (
                  <p key={warning} className="pdf-import-review-copy">
                    {warning}
                  </p>
                ))}
              </div>
              {onDismissImportReview && (
                <button
                  type="button"
                  className="pdf-import-review-dismiss"
                  onClick={onDismissImportReview}
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
          <div className="form-builder-canvas-header">
            <div>
              <h4 className="form-builder-canvas-title">Form canvas</h4>
              <p className="form-builder-canvas-subtitle">
                Click a field to edit · Drag to reorder
              </p>
            </div>
            {hasSections && (
              <div className="form-builder-canvas-stats">
                <span>{template.length} section{template.length !== 1 ? "s" : ""}</span>
                <span className="form-builder-canvas-stats-divider" aria-hidden="true" />
                <span>{totalFields} field{totalFields !== 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
          {activeDragKind && (
            <div className="form-builder-drag-hint" aria-live="polite">
              {activeDragKind === "palette" &&
                (hasSections
                  ? "Drop into a highlighted section, sub-section, or field row"
                  : "Drop Section on the canvas to begin")}
              {activeDragKind === "section" && "Drop to reorder sections"}
              {(activeDragKind === "field" || activeDragKind === "subfield") &&
                "Drop to reorder or move between sections and sub-sections"}
            </div>
          )}
          {canvas}
        </div>
        <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
          {activeId ? (
            <DragPreview activeId={activeId} template={template} />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default FormBuilderSectionList;
